import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetAllState } from "./resetAllState";
import {
  AnalyzedMediaResource,
  BulkImageAnalyzeResponse,
  ANALYZE_BATCH_SIZE,
} from "../../interfaces/ImageAnalyzeObject";
import {
  analyzeBatch,
  analyzeImages,
} from "../../services/thunks/ImageAnalyzeThunk";
import { analyzeBatchKey, analyzeImageKey } from "./ImageAnalyzeKeys";

/** Re-exported so callers reach for the keys next to the actions they guard,
 *  the way NineSetupDb exposes its own per-row keys. Defined in
 *  ImageAnalyzeKeys.ts to keep this file and ImageAnalyzeThunk out of a
 *  circular import — see that file. */
export { analyzeBatchKey, analyzeImageKey };

/**
 * State for the curator's photo-analysis flow (POST bulkImageAnalyze), used by
 * NineSetupAnalyzePanel inside the bulk-upload modal.
 *
 * Why this is a slice at all, rather than component state like the rest of that
 * modal: analysis is slow (5–20s per photo), expensive (a vision model call and
 * a permanent Supabase write per image), and runs in batches a curator kicks
 * off and then waits on. Three consequences fall out of that, and none of them
 * survive in useState:
 *
 *  - It has to keep running when the component isn't mounted. A thunk
 *    dispatched to the store runs to completion whether or not the modal is
 *    still open, so a curator can close it, check something on the business
 *    page, and come back to finished results. Nothing here is aborted on
 *    unmount, deliberately — see ImageAnalyzeThunk.
 *  - It has to survive a route change. Redux does that for free; component
 *    state does not, and losing a completed batch to a stray navigation means
 *    paying for it again.
 *  - It is keyed per setup, so two rows open in sequence never bleed into each
 *    other.
 *
 * The Files themselves are NOT here (see AnalyzeImageFileStore) — this slice
 * holds only serialisable records, which is also what makes persisting it
 * across a refresh possible later.
 */

// ── Records ──

/**
 * One picked photo.
 *
 * `queued`     picked, not yet sent
 * `analyzing`  in flight
 * `done`       analysed, at least one product came out of it
 * `unassigned` analysed fine, but the model built nothing from it. NOT a
 *              failure — the call worked and the photo is stored — so it reads
 *              amber rather than red, and retrying the same blurred frame is
 *              unlikely to help. It gets attach-or-discard rather than a plain
 *              retry, because a photo silently vanishing is the worst outcome
 *              in this flow.
 * `failed`     the call or the file itself was rejected. Retryable.
 * `orphaned`   state was rehydrated after a refresh but the bytes weren't.
 *              Nothing can be sent; the photo has to be picked again.
 */
export type AnalyzeImageStatus =
  | "queued"
  | "analyzing"
  | "done"
  | "unassigned"
  | "failed"
  | "orphaned";

export interface AnalyzeImageRecord {
  /** Client uuid, minted by AnalyzeImageFileStore.put. Stable for the life of
   *  the session: the React key, the file-store key, and what batches hold.
   *  Never the server's `img_N` ref, which is only meaningful within one call. */
  id: string;
  fileName: string;
  sizeBytes: number;
  mimeType: string;
  status: AnalyzeImageStatus;
  /** Backend code (or ours, e.g. ANALYZE_FILE_MISSING). Drives the tile's copy
   *  via analyzeErrorCopy — never rendered raw. */
  errorCode: string | null;
  /** The server's own errorDetail, kept as a fallback for a code this build
   *  doesn't recognise. */
  errorMessage: string | null;
  /** The call this image was analysed in. Worth quoting in a bug report — it
   *  keys every server log line and the storage folder. Per-image because each
   *  photo is currently sent in its own call. */
  analysisId: string | null;
  /** The `img_N` ref within that call. Only ever img_0 while one image is sent
   *  per call, but stored rather than assumed so a future batched call needs no
   *  change here. */
  serverRef: string | null;
  /** Supabase URL of the stored original. Once this is set the tile no longer
   *  needs the local File, which is what lets a refresh keep its thumbnail. */
  publicUrl: string | null;
  /** The stored media record, identical to the entries inside a draft's
   *  `productMediaResources`. Kept even for an UNASSIGNED photo — that is
   *  exactly the one the curator may want to attach to a product by hand, and
   *  it is already in storage whether or not the model used it. */
  mediaResource: AnalyzedMediaResource | null;
  /** Set when an unassigned photo has been attached to a product by hand. The
   *  photo produced nothing itself, so its status stays `unassigned`; this is
   *  what stops it reading as an abandoned upload. */
  attachedToProductId: string | null;
  /** Ids into `products` — what this photo produced. Cleared and rebuilt on
   *  every re-analysis so a retry can't leave a duplicate set behind. */
  productIds: string[];
}

export interface AnalyzeProductRecord {
  /** Client uuid — the server's `prod_N` is only unique within one call, and
   *  with one call per image every product in the session would otherwise be
   *  prod_0. */
  id: string;
  /**
   * The AnalyzeImageRecord.id of this product's HERO photo — the client id
   * behind the response's `primaryImageRef`.
   *
   * Not "the only photo it came from": with several images per call the model
   * may build one product from a set, and the full membership lives on the
   * image side, in `AnalyzeImageRecord.productIds`. Read it from there (filter
   * images whose productIds contain this product) rather than assuming this
   * field is the whole story.
   */
  sourceImageId: string;
  /** The server's `prod_N`, kept for bug reports alongside analysisId. */
  serverRef: string;
  confidence: number;
  missingRequiredFields: string[];
  /** The CreateProductRq, stored verbatim and posted back untouched. Curator
   *  edits mutate fields on this object; nothing rebuilds it. `unknown` keeps
   *  the blob opaque without also switching off type-checking at the places
   *  that read it — see AnalyzedProductDraft. */
  draft: Record<string, unknown>;
  /** Curator can drop a product before handing off to the payload. Defaults to
   *  accepted — the common correction is dropping one, not keeping one. */
  accepted: boolean;
}

/**
 * `idle`    never run, or every member is still queued
 * `running` a run is in flight
 * `done`    every member analysed cleanly
 * `partial` the run finished with at least one member failed or unassigned
 */
export type AnalyzeBatchStatus = "idle" | "running" | "done" | "partial";

export interface AnalyzeBatchRecord {
  id: string;
  /** 1-based, for "Analyse batch 2". */
  index: number;
  /** Up to ANALYZE_BATCH_SIZE image ids, in pick order. */
  imageIds: string[];
  status: AnalyzeBatchStatus;
}

export interface AnalyzeSession {
  setupId: string;
  businessId: string | null;
  images: Record<string, AnalyzeImageRecord>;
  /** Pick order. `images` is a map for O(1) updates during a run; this is what
   *  the UI iterates. */
  imageOrder: string[];
  batches: AnalyzeBatchRecord[];
  products: Record<string, AnalyzeProductRecord>;
  /** Products in source-image pick order, then in the order the call returned
   *  them. Sorted on write so the review list reads in the same order as the
   *  photo grid above it, rather than in whatever order batches happened to
   *  finish. */
  productOrder: string[];
  createdAt: number;
}

export interface ImageAnalyzeState {
  sessions: Record<string, AnalyzeSession>;
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  /**
   * No `successMap`, unlike the other slices in this app.
   *
   * Deliberate rather than an omission: per-image success is already carried by
   * `AnalyzeImageRecord.status`, which distinguishes `done` from `unassigned`
   * from `failed`. A parallel boolean could only ever say less than that, and
   * would introduce a second thing to keep in step — one that can disagree with
   * the status a tile actually renders.
   */
}

const initialState: ImageAnalyzeState = {
  sessions: {},
  loadingMap: {},
  errorMap: {},
};

// ── Helpers ──

const emptySession = (setupId: string, businessId: string | null): AnalyzeSession => ({
  setupId,
  businessId,
  images: {},
  imageOrder: [],
  batches: [],
  products: {},
  productOrder: [],
  createdAt: Date.now(),
});

/**
 * Appends images to the last batch that can still take them, opening new ones
 * as needed.
 *
 * Deliberately NOT a re-chunk of the whole list into fives. Re-chunking would
 * reshuffle membership every time a photo is added or removed, which means a
 * batch a curator already ran could silently gain an unanalysed member — or
 * worse, a finished batch could be renumbered under them mid-flow. Assign once,
 * never move: a batch that has started is closed to new images, and removing an
 * image just leaves that batch one short.
 */
const assignToBatches = (
  session: AnalyzeSession,
  imageIds: readonly string[]
): void => {
  imageIds.forEach((imageId) => {
    const open = session.batches.find(
      (batch) =>
        batch.status === "idle" && batch.imageIds.length < ANALYZE_BATCH_SIZE
    );
    if (open) {
      open.imageIds.push(imageId);
      return;
    }
    session.batches.push({
      id: crypto.randomUUID(),
      index: session.batches.length + 1,
      imageIds: [imageId],
      status: "idle",
    });
  });
};

/** Batch status from its members, so a single-image retry that fixes the last
 *  red tile flips its batch from `partial` to `done` without the batch runner
 *  being involved. */
const recomputeBatchStatus = (
  session: AnalyzeSession,
  batch: AnalyzeBatchRecord
): void => {
  const statuses = batch.imageIds
    .map((id) => session.images[id]?.status)
    .filter((status): status is AnalyzeImageStatus => !!status);

  if (statuses.length === 0) {
    batch.status = "idle";
    return;
  }
  if (statuses.some((status) => status === "analyzing")) {
    batch.status = "running";
    return;
  }
  if (statuses.every((status) => status === "queued")) {
    batch.status = "idle";
    return;
  }
  if (statuses.some((status) => status !== "done")) {
    batch.status = "partial";
    return;
  }
  batch.status = "done";
};

const batchOfImage = (
  session: AnalyzeSession,
  imageId: string
): AnalyzeBatchRecord | undefined =>
  session.batches.find((batch) => batch.imageIds.includes(imageId));

/** Recompute every batch touched by a group of images. A group comes from one
 *  batch today, but nothing in the types guarantees that, and recomputing a
 *  batch is cheap. */
const touchedBatches = (session: AnalyzeSession, imageIds: string[]): void => {
  const seen = new Set<string>();
  imageIds.forEach((imageId) => {
    const batch = batchOfImage(session, imageId);
    if (!batch || seen.has(batch.id)) return;
    seen.add(batch.id);
    recomputeBatchStatus(session, batch);
  });
};

/** Keeps productOrder in photo order rather than completion order — the review
 *  list then reads top-to-bottom the same way the grid above it does. */
const resortProductOrder = (session: AnalyzeSession): void => {
  const imagePosition = new Map(session.imageOrder.map((id, index) => [id, index]));
  session.productOrder.sort((a, b) => {
    const left = imagePosition.get(session.products[a]?.sourceImageId ?? "") ?? 0;
    const right = imagePosition.get(session.products[b]?.sourceImageId ?? "") ?? 0;
    if (left !== right) return left - right;
    return (session.products[a]?.serverRef ?? "").localeCompare(
      session.products[b]?.serverRef ?? ""
    );
  });
};

/** Drops everything a previous analysis of this image produced. Called before
 *  every (re)run so a retry replaces its products instead of doubling them. */
const dropProductsOfImage = (session: AnalyzeSession, imageId: string): void => {
  const image = session.images[imageId];
  if (!image) return;
  const doomed = [...image.productIds];

  doomed.forEach((productId) => {
    // Any OTHER photo attached by hand to a product that is about to vanish
    // has to let go of it, or it keeps pointing at a product id nothing can
    // resolve for the rest of the session.
    Object.values(session.images).forEach((other) => {
      if (other.attachedToProductId === productId) other.attachedToProductId = null;
    });
    delete session.products[productId];
  });

  session.productOrder = session.productOrder.filter(
    (productId) => !doomed.includes(productId)
  );

  // A product built from several photos is listed on every one of them, so
  // dropping it has to clear it from all of them — not just the image being
  // re-analysed. Missing this would leave a sibling tile counting a product
  // that no longer exists.
  Object.values(session.images).forEach((other) => {
    other.productIds = other.productIds.filter((id) => !doomed.includes(id));
  });
};

/** Removes an image's media record from a draft's productMediaResources.
 *  Matched on objectPath rather than identity — the draft's copy came from the
 *  same response but is a different object once it has been through Immer. */
const pullMediaFromDraft = (
  draft: Record<string, unknown>,
  media: AnalyzedMediaResource | null
): void => {
  if (!media || !Array.isArray(draft.productMediaResources)) return;
  // Narrowed to the one field this compares on, rather than `any`: the array
  // holds whatever the backend put there, and only `objectPath` is load-bearing
  // here.
  const entries = draft.productMediaResources as { objectPath?: string | null }[];
  draft.productMediaResources = entries.filter(
    (entry) => entry?.objectPath !== media.objectPath
  );
};

/**
 * Folds one call's response into the session.
 *
 * Handles 1..N images per request. The correspondence between what was sent and
 * what came back is POSITIONAL: files are appended in `imageIds` order and the
 * backend assigns `img_0..img_N` in upload order. The numeric suffix is used
 * where it parses — that is the documented scheme and it survives an entry
 * being reordered — and array position is the fallback for a ref shaped
 * unexpectedly, so a contract change degrades instead of mis-attributing.
 *
 * The order of work matters. Every product is created BEFORE any image's status
 * is decided, because with grouped calls an image's outcome depends on whether
 * a product ended up claiming it — and the product that claims it may be the
 * one built primarily from a different photo.
 */
const applyAnalyzeResponse = (
  session: AnalyzeSession,
  imageIds: string[],
  response: BulkImageAnalyzeResponse
): void => {
  const analysisId = response.analysisId ?? null;

  /** Server ref (`img_0`) → the client id it was sent as. */
  const clientIdByRef = new Map<string, string>();
  /** Client ids that got no entry back at all. */
  const unanswered = new Set(imageIds);

  (response.images ?? []).forEach((analyzed, position) => {
    const suffix = Number.parseInt(String(analyzed?.ref ?? "").replace(/^img_/, ""), 10);
    const index = Number.isInteger(suffix) && suffix >= 0 && suffix < imageIds.length
      ? suffix
      : position;
    const clientId = imageIds[index];
    if (!clientId || !analyzed?.ref) return;
    clientIdByRef.set(analyzed.ref, clientId);
    unanswered.delete(clientId);
  });

  /** The error entry naming a given ref, if any. */
  const errorForRef = (ref: string | undefined) =>
    (response.errors ?? []).find(
      (entry) => ref && entry.additionalDetails?.imageRef === ref
    );

  // ── 1. Stamp what the server said about each image, and settle failures ──
  const failed = new Set<string>();

  (response.images ?? []).forEach((analyzed) => {
    const clientId = analyzed?.ref ? clientIdByRef.get(analyzed.ref) : undefined;
    const image = clientId ? session.images[clientId] : undefined;
    if (!image || !clientId) return;

    image.analysisId = analysisId;
    image.serverRef = analyzed.ref ?? null;
    image.publicUrl = analyzed.publicUrl ?? null;
    image.mediaResource = analyzed.mediaResource ?? null;

    if (analyzed.status === "FAILED") {
      const entry = errorForRef(analyzed.ref);
      image.status = "failed";
      image.errorCode = entry?.errorCode ?? "IMAGE_ANALYZE_UPLOAD_FAILED";
      image.errorMessage = entry?.errorDetail ?? response.error ?? null;
      failed.add(clientId);
    }
  });

  // A photo the response never mentioned. Should not happen — the contract says
  // every uploaded file comes back whatever its outcome — but leaving a tile on
  // "analyzing" forever is the one failure mode with no way out for the curator.
  unanswered.forEach((clientId) => {
    const image = session.images[clientId];
    if (!image) return;
    image.analysisId = analysisId;
    image.status = "failed";
    image.errorCode = "IMAGE_ANALYZE_UPLOAD_FAILED";
    image.errorMessage = "The server returned no result for this photo.";
    failed.add(clientId);
  });

  // ── 2. Create products, listing each on EVERY photo it was built from ──
  (response.products ?? []).forEach((product) => {
    const memberIds = (product.imageRefs ?? [])
      .map((ref) => clientIdByRef.get(ref))
      .filter((id): id is string => !!id && !!session.images[id]);

    // Every ref is server-validated against the real upload set, so an empty
    // membership here means the product referenced only photos from some other
    // call — nothing this session can render it against.
    if (memberIds.length === 0) return;

    const heroId = (product.primaryImageRef
      ? clientIdByRef.get(product.primaryImageRef)
      : undefined) ?? memberIds[0];

    const id = crypto.randomUUID();
    session.products[id] = {
      id,
      sourceImageId: heroId,
      serverRef: product.ref,
      confidence: product.confidence ?? 0,
      missingRequiredFields: product.missingRequiredFields ?? [],
      // Verbatim. Never rebuilt, never filtered — see ImageAnalyzeObject.ts.
      draft: product.draft?.product ?? {},
      accepted: true,
    };
    session.productOrder.push(id);
    memberIds.forEach((memberId) => session.images[memberId].productIds.push(id));
  });

  // ── 3. Now every non-failed image's outcome is knowable ──
  imageIds.forEach((clientId) => {
    const image = session.images[clientId];
    if (!image || failed.has(clientId)) return;

    if (image.productIds.length > 0) {
      image.status = "done";
      image.errorCode = null;
      image.errorMessage = null;
      return;
    }

    // Stored fine, but nothing was built from it. Amber, not red: the call
    // worked and the photo is in storage, so this gets attach-or-discard in the
    // review rather than a retry that would reach the same answer.
    const entry = errorForRef(image.serverRef ?? undefined);
    image.status = "unassigned";
    image.errorCode = entry?.errorCode ?? "IMAGE_ANALYZE_IMAGE_UNASSIGNED";
    image.errorMessage = entry?.errorDetail ?? null;
  });

  resortProductOrder(session);
};

// ── Slice ──

const imageAnalyzeSlice = createSlice({
  name: "imageAnalyze",
  initialState,
  reducers: {
    /** Creates the session if it doesn't exist. Called lazily on first pick
     *  rather than on modal open, so opening and closing the modal without
     *  picking anything leaves no state behind. */
    startAnalyzeSession: (
      state,
      action: PayloadAction<{ setupId: string; businessId: string | null }>
    ) => {
      const { setupId, businessId } = action.payload;
      if (!state.sessions[setupId]) {
        state.sessions[setupId] = emptySession(setupId, businessId);
        return;
      }
      // A business can be attached after the session started (the modal fetches
      // it asynchronously), so keep the latest non-null value.
      if (businessId) state.sessions[setupId].businessId = businessId;
    },

    addAnalyzeImages: (
      state,
      action: PayloadAction<{
        setupId: string;
        businessId?: string | null;
        images: {
          id: string;
          fileName: string;
          sizeBytes: number;
          mimeType: string;
        }[];
      }>
    ) => {
      const { setupId, businessId, images } = action.payload;
      if (!state.sessions[setupId]) {
        state.sessions[setupId] = emptySession(setupId, businessId ?? null);
      }
      const session = state.sessions[setupId];

      const added: string[] = [];
      images.forEach((image) => {
        if (session.images[image.id]) return;
        session.images[image.id] = {
          ...image,
          status: "queued",
          errorCode: null,
          errorMessage: null,
          analysisId: null,
          serverRef: null,
          publicUrl: null,
          mediaResource: null,
          attachedToProductId: null,
          productIds: [],
        };
        session.imageOrder.push(image.id);
        added.push(image.id);
      });

      assignToBatches(session, added);
    },

    /** Removes a photo and everything it produced. The File itself is dropped
     *  by the caller (AnalyzeImageFileStore.remove) — a reducer can't have that
     *  side effect. */
    removeAnalyzeImage: (
      state,
      action: PayloadAction<{ setupId: string; imageId: string }>
    ) => {
      const { setupId, imageId } = action.payload;
      const session = state.sessions[setupId];
      if (!session) return;

      // Detach before the record goes, or its media stays on a draft with
      // nothing left pointing at it.
      const removed = session.images[imageId];
      if (removed?.attachedToProductId) {
        const host = session.products[removed.attachedToProductId];
        if (host) pullMediaFromDraft(host.draft, removed.mediaResource);
      }

      dropProductsOfImage(session, imageId);
      delete session.images[imageId];
      session.imageOrder = session.imageOrder.filter((id) => id !== imageId);

      session.batches.forEach((batch) => {
        const index = batch.imageIds.indexOf(imageId);
        if (index >= 0) batch.imageIds.splice(index, 1);
        recomputeBatchStatus(session, batch);
      });
      // A batch emptied entirely is dropped, but the ones after it keep their
      // ids — only the displayed index is renumbered, so nothing in flight is
      // pointing at a batch that moved.
      session.batches = session.batches.filter((batch) => batch.imageIds.length > 0);
      session.batches.forEach((batch, index) => {
        batch.index = index + 1;
      });

      delete state.loadingMap[analyzeImageKey(setupId, imageId)];
      delete state.errorMap[analyzeImageKey(setupId, imageId)];
    },

    /**
     * Attaches an unassigned photo to a product by hand.
     *
     * The photo is already in storage — an UNASSIGNED status means the model
     * built nothing from it, not that it failed to upload — so this is purely
     * a matter of appending its media record to the target draft. Appended
     * rather than inserted first: the model already chose that product's hero
     * shot, and a photo it declined to use is not a better one.
     *
     * Re-attaching moves it: the previous product loses the record before the
     * new one gains it, so a curator changing their mind can't leave the same
     * photo on two products.
     */
    attachImageToProduct: (
      state,
      action: PayloadAction<{
        setupId: string;
        imageId: string;
        productId: string | null;
      }>
    ) => {
      const { setupId, imageId, productId } = action.payload;
      const session = state.sessions[setupId];
      const image = session?.images[imageId];
      if (!session || !image) return;

      if (image.attachedToProductId) {
        const previous = session.products[image.attachedToProductId];
        if (previous) pullMediaFromDraft(previous.draft, image.mediaResource);
      }

      image.attachedToProductId = null;
      if (!productId) return;

      const target = session.products[productId];
      if (!target || !image.mediaResource) return;

      if (!Array.isArray(target.draft.productMediaResources)) {
        target.draft.productMediaResources = [];
      }
      pullMediaFromDraft(target.draft, image.mediaResource);
      (target.draft.productMediaResources as AnalyzedMediaResource[]).push(
        image.mediaResource
      );
      image.attachedToProductId = productId;
    },

    setProductAccepted: (
      state,
      action: PayloadAction<{
        setupId: string;
        productId: string;
        accepted: boolean;
      }>
    ) => {
      const { setupId, productId, accepted } = action.payload;
      const product = state.sessions[setupId]?.products[productId];
      if (product) product.accepted = accepted;
    },

    /**
     * Writes a curator's edit into a draft.
     *
     * Takes a path so a nested `details.fuel_type` can be set without the
     * caller rebuilding the draft around it — the one rule this whole feature
     * is organised around is that the draft object is edited in place and never
     * reconstructed.
     */
    updateProductDraftField: (
      state,
      action: PayloadAction<{
        setupId: string;
        productId: string;
        path: string[];
        value: unknown;
      }>
    ) => {
      const { setupId, productId, path, value } = action.payload;
      const product = state.sessions[setupId]?.products[productId];
      if (!product || path.length === 0) return;

      // The one genuinely dynamic write in this slice, and the one place `any`
      // is still the honest type: the path is data, so neither the key nor the
      // value can be known here. Narrowed to this walker rather than widened to
      // the draft type it walks.
      let target: Record<string, any> = product.draft;
      for (let i = 0; i < path.length - 1; i += 1) {
        const key = path[i];
        if (!target[key] || typeof target[key] !== "object") target[key] = {};
        target = target[key];
      }
      target[path[path.length - 1]] = value;
    },

    /**
     * Restores sessions read back from storage, once, at store creation.
     *
     * Two things happen here that a plain state merge could not, which is why
     * this is a reducer rather than `preloadedState`:
     *
     * 1. Every image that had not finished is marked `orphaned`. Its `File`
     *    lived in AnalyzeImageFileStore, a module-level Map that died with the
     *    page, so there is nothing left to send — offering a retry would be a
     *    button that can only fail. Images that reached `done` or `unassigned`
     *    are untouched: the backend already stored those photos and the record
     *    points at the stored copy, so they need no bytes.
     * 2. Batch statuses are recomputed from their members, using the same
     *    helper every other path uses. A batch that was mid-run when the tab
     *    reloaded must not come back as `running`, or its button stays disabled
     *    behind a spinner for a call that nobody is waiting on.
     *
     * loadingMap and errorMap are deliberately NOT restored — they describe
     * requests that died with the page.
     */
    rehydrateAnalyzeSessions: (
      state,
      action: PayloadAction<Record<string, AnalyzeSession>>
    ) => {
      const sessions = action.payload;
      if (!sessions || Object.keys(sessions).length === 0) return;

      Object.values(sessions).forEach((session) => {
        Object.values(session.images).forEach((image) => {
          if (image.status === "done" || image.status === "unassigned") return;
          image.status = "orphaned";
          image.errorCode = "ANALYZE_FILE_MISSING";
          image.errorMessage = null;
        });
        session.batches.forEach((batch) => recomputeBatchStatus(session, batch));
      });

      state.sessions = sessions;
      state.loadingMap = {};
      state.errorMap = {};
    },

    clearAnalyzeSession: (state, action: PayloadAction<{ setupId: string }>) => {
      const { setupId } = action.payload;
      delete state.sessions[setupId];
      Object.keys(state.loadingMap).forEach((key) => {
        if (key.includes(setupId)) delete state.loadingMap[key];
      });
      Object.keys(state.errorMap).forEach((key) => {
        if (key.includes(setupId)) delete state.errorMap[key];
      });
    },
  },

  extraReducers: (builder) => {
    builder
      // ── One request, carrying one or more photos ──
      //
      // Every case below iterates `imageIds`, so a single-photo retry and a full
      // group take the exact same path — a retry is just a group of one. That is
      // what keeps the two from drifting apart.
      .addCase(analyzeImages.pending, (state, action) => {
        const { setupId, imageIds } = action.meta.arg;
        const session = state.sessions[setupId];

        imageIds.forEach((imageId) => {
          const key = analyzeImageKey(setupId, imageId);
          state.loadingMap[key] = "loading";
          state.errorMap[key] = null;

          const image = session?.images[imageId];
          if (!session || !image) return;

          // A retry replaces its products rather than adding a second set.
          dropProductsOfImage(session, imageId);
          image.status = "analyzing";
          image.errorCode = null;
          image.errorMessage = null;
        });

        if (session) touchedBatches(session, imageIds);
      })
      .addCase(analyzeImages.fulfilled, (state, action) => {
        const { setupId, imageIds } = action.meta.arg;
        imageIds.forEach((imageId) => {
          state.loadingMap[analyzeImageKey(setupId, imageId)] = null;
        });

        const session = state.sessions[setupId];
        if (!session) return;

        applyAnalyzeResponse(session, imageIds, action.payload.response);
        touchedBatches(session, imageIds);
      })
      .addCase(analyzeImages.rejected, (state, action) => {
        const { setupId, imageIds } = action.meta.arg;
        const rejected = action.payload as
          | { errorCode?: string; message?: string }
          | undefined;
        const message = rejected?.message ?? action.error.message ?? null;
        const session = state.sessions[setupId];

        // The request failed as a whole, so every photo in it failed — there is
        // no per-image detail to distribute when the call never returned one.
        imageIds.forEach((imageId) => {
          const key = analyzeImageKey(setupId, imageId);
          state.loadingMap[key] = null;
          state.errorMap[key] = message;

          const image = session?.images[imageId];
          if (!session || !image) return;

          image.status =
            rejected?.errorCode === "ANALYZE_FILE_MISSING" ? "orphaned" : "failed";
          image.errorCode = rejected?.errorCode ?? null;
          image.errorMessage = message;
        });

        if (session) touchedBatches(session, imageIds);
      })

      // ── One batch ──
      //
      // The batch thunk owns only sequencing; every state change for the images
      // inside it arrives through the per-image cases above. So these three
      // cases carry the batch's own loading key and nothing else.
      .addCase(analyzeBatch.pending, (state, action) => {
        const { setupId, batchId } = action.meta.arg;
        const key = analyzeBatchKey(setupId, batchId);
        state.loadingMap[key] = "loading";
        state.errorMap[key] = null;

        const batch = state.sessions[setupId]?.batches.find((b) => b.id === batchId);
        if (batch) batch.status = "running";
      })
      .addCase(analyzeBatch.fulfilled, (state, action) => {
        const { setupId, batchId } = action.meta.arg;
        state.loadingMap[analyzeBatchKey(setupId, batchId)] = null;

        const session = state.sessions[setupId];
        const batch = session?.batches.find((b) => b.id === batchId);
        if (session && batch) recomputeBatchStatus(session, batch);
      })
      .addCase(analyzeBatch.rejected, (state, action) => {
        const { setupId, batchId } = action.meta.arg;
        const key = analyzeBatchKey(setupId, batchId);
        state.loadingMap[key] = null;
        state.errorMap[key] = action.error.message ?? "That batch couldn't be run.";

        const session = state.sessions[setupId];
        const batch = session?.batches.find((b) => b.id === batchId);
        if (session && batch) recomputeBatchStatus(session, batch);
      })

      .addCase(resetAllState, () => initialState);
  },
});

export const {
  startAnalyzeSession,
  addAnalyzeImages,
  attachImageToProduct,
  removeAnalyzeImage,
  setProductAccepted,
  updateProductDraftField,
  rehydrateAnalyzeSessions,
  clearAnalyzeSession,
} = imageAnalyzeSlice.actions;

export default imageAnalyzeSlice.reducer;
